<script setup lang="ts">
import AppTopBar from "./components/AppTopBar.vue";
import EmailVerificationBanner from "./components/EmailVerificationBanner.vue";
import AppUpdateBanner from "./components/AppUpdateBanner.vue";
import ConfirmDialog from "./components/ConfirmDialog.vue";
import { routeViewKey } from "./utils/routeViewKey.js";
</script>

<template>
  <AppTopBar />
  <AppUpdateBanner />
  <EmailVerificationBanner />
  <!-- Keyed per route meta.remount: a page that sets itself up for one game id
       (or one /local mode) is rebuilt for the next instead of being reused
       with the previous one's state, socket and timers. -->
  <router-view v-slot="{ Component, route }">
    <component :is="Component" :key="routeViewKey(route)" />
  </router-view>
  <ConfirmDialog />
</template>

<style>
@import "./styles/workspace-shell.css";

/* Design tokens — single source of truth for the app's look. */
:root {
  /* "Matchday" design language: dark-green football aesthetic. */
  --bg: #0a0d0b;
  --bg-raised: #0f1a13;
  --surface: rgba(255, 255, 255, 0.02);
  --border: #1d3226;
  --border-muted: #1d2620;
  --border-strong: #22422f;
  --text: #e7f0ea;
  --text-strong: #f2fbf4;
  --text-muted: #83988c;
  --text-soft: #9fb0a6;
  --text-faint: #5f7267;
  --text-body: #cfe0d5;
  --text-on-accent: #eafff2;
  --border-soft: #223b31;
  --border-accent: #2d5a3f;
  --border-accent-strong: #315a43;
  --team-a-tint: #8fb6ff;
  --team-b-tint: #ffb0ae;
  --text-dim: #a7b8ad;
  --accent-soft: #a8e6c1;
  --danger: #ef4444;
  --accent: #8ecfa8;
  --accent-strong: #34d27b;
  --link: #8ecfa8;
  --green: #176c3c;
  --green-hover: #25a35c;
  --success-text: #8ecfa8;
  --danger-bg: rgba(142, 52, 56, 0.2);
  --danger-border: #7a2f2e;
  --danger-text: #ff9fa2;
  --radius-sm: 8px;
  --radius-md: 11px;
  --radius-lg: 15px;
  --control-bg: #0d1712;
  --control-bg-hover: #14261b;
  --control-border: #22422f;
  --panel-bg: rgba(255, 255, 255, 0.02);
  --panel-bg-strong: #0f1a13;
  --panel-border: #1d2620;
  --mobile-bar-bg: #0d1310;
  --mobile-bar-border: #17251c;
  --team-a: #4f8ff5;
  --team-a-soft: #7bafff;
  --team-a-border: #8fbaff;
  --team-b: #ef5350;
  --team-b-soft: #f08a8a;
  --team-b-border: #ff9a99;
  --pitch: #3c8747;
  --pitch-check: #43974f;
  --gold: #f4c95d;
  --gold-text: #e2ca7a;
  --gold-border: #2a2618;
  --font-body: "Barlow", system-ui, sans-serif;
  --font-display: "Barlow Condensed", "Barlow", system-ui, sans-serif;
  --font-mono: "JetBrains Mono", Consolas, monospace;
  /* Primary card: linear-gradient(180deg, #0f1a13, #0b0f0c) + --border. */
  --card-gradient: linear-gradient(180deg, #0f1a13, #0b0f0c);
  /* Primary CTA gradient + border --accent-strong. */
  --cta-gradient: linear-gradient(180deg, #25a35c, #157a41);
  --cta-gradient-hover: linear-gradient(180deg, #2bb367, #1a8a4a);
  --workspace-max-width: 1600px;
  --workspace-padding: 16px;
  --workspace-gap: 24px;
  --workspace-sidebar-width: 260px;
  --workspace-sidebar-gap: 10px;
  --workspace-sidebar-top: 4px;
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  background: var(--bg);
  color: var(--text);
  font-family: var(--font-body);
}

body {
  display: flex;
  flex-direction: column;
  /* One shared page backdrop so routes don't flash between different backgrounds. */
  background: radial-gradient(circle at 15% 0%, #0d1710 0%, var(--bg) 55%) fixed no-repeat var(--bg);
}

::-webkit-scrollbar { width: 10px; height: 10px; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 6px; }

@keyframes livePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(86, 216, 135, 0.5); }
  50% { box-shadow: 0 0 0 5px rgba(86, 216, 135, 0); }
}

/* Keyboard focus should always be visible. */
:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* Default link color for body text; component-scoped styles still override. */
a {
  color: var(--link);
}

/* Shared layout for simple content pages (About, Impressum, Privacy Policy). */
.content-page {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  padding: 24px 16px 60px;
  box-sizing: border-box;
}

.content-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 20px;
  line-height: 1.55;
}

.content-card h1 {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-size: 2.1rem;
  color: var(--text-strong);
}

.content-card h2 {
  margin: 20px 0 8px;
  font-size: 1.1rem;
}

.content-card p,
.content-card li {
  color: var(--text-body);
}

.content-card ul {
  margin: 8px 0 0 18px;
  padding: 0;
}

.content-card .subtitle {
  color: var(--text-muted);
  margin: 6px 0 18px;
}

#app {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
